import { HttpClient, HttpContext, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, shareReplay, map, catchError, of } from "rxjs";
import { APIResponse } from "src/app/core/models/api-response";
import { IAskForQuotation, INewQuotation, IQuotationDetail, IQuotationFilterRequest, IQuotationsKPIs, IQuotationSourceContent, ISentToErpRespone, IUpdateEmailOrder, IUpdateAiNotes, IUpdateQuotationDetails, IQuotationAvailabilityReq, IDeleteDuplicateProducts, IPromptToOrder, IUpdateQuotationLinesPayload, IOrderLineAlert, IQuotationAlertsResponse, IParseProduct, IHistory, IQuotationDealsResponse, ILineStockResponse, IUpdateLinesDelta, IDocumentCoordinatesResponse } from "../models/quotations.models";
import { environment } from "src/environments/environment";
import { NumberSymbol } from "@angular/common";
import { IQuotePageCount, IPagination } from "src/app/shared/models/page.model";
import { skipErrorToast } from "src/app/core/http/error-toast.token";

@Injectable({
  providedIn: 'root',
})
export class QuotesService {
  /** One shared Observable per quotation ID — first subscriber triggers the request, rest reuse the replay. */
  private _alternativesBulkCache = new Map<number, Observable<APIResponse<Record<string, IParseProduct[]>>>>();
  /** Same per-quotation caching for the UNMATCHED-lines alternatives endpoint. */
  private _unmatchedAlternativesBulkCache = new Map<number, Observable<APIResponse<Record<string, IParseProduct[]>>>>();

  constructor(private http: HttpClient) { }

  public getQuotations(
    page: IQuotePageCount,
    filter: IQuotationFilterRequest | undefined,
    search: string | undefined,
    sort_by: string = 'id',
    sort_mode: string = 'desc',
    status: string = '',
    archived: number = 0
  ): Observable<APIResponse<IQuotationsKPIs>> {
    let params = new HttpParams()
      .set('page', page.number)
      .set('limit', page.size)
      .set('co-pilot', page.co_pilot)
      .set('order', page.order)
      .set('filters', JSON.stringify(filter ?? { filters: [] }))
      .set('sort_by', sort_by!)
      .set('sort_mode', sort_mode!)
      .set('status', status)
      .set('archived', archived);
    const key = (search ?? '').trim();
    if (key) {
      params = params.set('key', key);
    }
    return this.http.get<APIResponse<IQuotationsKPIs>>(
      environment.BASE_URL_API + 'quotations',
      { params }
    );
  }

  public markQuotationRead(id: number): Observable<APIResponse<{ id: number; is_read: boolean }>> {
    return this.http.patch<APIResponse<{ id: number; is_read: boolean }>>(
      `${environment.BASE_URL_API}quotations/${id}/read`,
      {}
    );
  }

  public dismissDuplicateBadge(id: number): Observable<APIResponse<{ id: number; is_duplicate_dismissed: boolean }>> {
    return this.http.patch<APIResponse<{ id: number; is_duplicate_dismissed: boolean }>>(
      `${environment.BASE_URL_API}quotations/${id}/dismiss-duplicate`,
      {}
    );
  }

  public getQuotationById(
    id: number,
    paginate_lines?: boolean,
    page?: number,
    limit?: number,
    include_ai_details?: boolean,
  ): Observable<APIResponse<IQuotationDetail>> {
    let params = new HttpParams();
    if (paginate_lines !== undefined) {
      params = params.set('paginate_lines', paginate_lines);
    }
    if (page !== undefined) {
      params = params.set('page', page);
    }
    if (limit !== undefined) {
      params = params.set('limit', limit);
    }
    if (include_ai_details === false) {
      params = params.set('include_ai_details', 'false');
    }
    return this.http.get<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id,
      { params }
    );
  }

  public updateQuotationDetails(id: number, payload: IUpdateQuotationDetails): Observable<APIResponse<Partial<IQuotationDetail>>> {
    return this.http.patch<APIResponse<Partial<IQuotationDetail>>>(
      environment.BASE_URL_API + 'quotations/' + id,
      payload
    );
  }

  public sendQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/send',
      {}
    );
  }

  public dismissSuggestedLocation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/ai_details/location/dismiss',
      {}
    );
  }

  public resendQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/resent',
      {}
    );
  }

  public sendNewQuotation(newQuote: INewQuotation): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/send-new-quotation',
      newQuote
    );
  }

  public draftNewQuotation(newQuote: INewQuotation): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/draft-new-quotation',
      newQuote
    );
  }

  public editDraftQuotation(id: number, quotation: IAskForQuotation): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/edit-and-draft',
      quotation
    );
  }

  public editQuotation(id: number, quotation: IAskForQuotation, updateStatus: string = '1'): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/edit',
      quotation, { params: new HttpParams().set('update_status', updateStatus) }
    );
  }

  public editAndSendQuotation(id: number, quotation: IAskForQuotation): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/edit-and-send',
      quotation
    );
  }

  public validateOrder(id: number, shouldDelete?: boolean, force?: boolean, forceSend?: boolean): Observable<APIResponse<ISentToErpRespone>> {
    let params = new HttpParams();
    if (shouldDelete) params = params.append('delete', shouldDelete.toString());
    if (force) params = params.append('force', force.toString());

    const body: Record<string, unknown> = {};
    if (forceSend) body['force_send'] = true;

    // The effect renders the ERP's own rejection wording itself, so the
    // global interceptor must stay out of it or the user sees the failure twice.
    return this.http.post<APIResponse<ISentToErpRespone>>(
      environment.BASE_URL_API + 'quotations/' + id + '/approve',
      Object.keys(body).length ? body : null,
      { params, context: skipErrorToast() }
    );
  }

  public cancelQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/cancel',
      {}
    );
  }

  public deleteQuotation(id: NumberSymbol, confirm?: boolean): Observable<APIResponse<IQuotationDetail>> {
    return this.http.delete<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/delete',
      { body: { confirm: confirm } }
    );
  }

  public duplicateQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/duplicate',
      {}
    );
  }

  public archiveQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/archive',
      {}
    );
  }

  public unarchiveQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/unarchive',
      {}
    );
  }

  public bulkDuplicateQuotations(ids: number[]): Observable<APIResponse<any>> {
    return this.http.post<APIResponse<any>>(
      environment.BASE_URL_API + 'quotations/bulk/duplicate',
      { ids }
    );
  }

  public bulkArchiveQuotations(ids: number[]): Observable<APIResponse<any>> {
    return this.http.patch<APIResponse<any>>(
      environment.BASE_URL_API + 'quotations/bulk/archive',
      { ids }
    );
  }

  public bulkUnarchiveQuotations(ids: number[]): Observable<APIResponse<any>> {
    return this.http.patch<APIResponse<any>>(
      environment.BASE_URL_API + 'quotations/bulk/unarchive',
      { ids }
    );
  }

  public bulkDeleteQuotations(ids: number[]): Observable<APIResponse<any>> {
    return this.http.delete<APIResponse<any>>(
      environment.BASE_URL_API + 'quotations/bulk/delete',
      { body: { ids, confirm: true } }
    );
  }

  public invoiceQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/invoice',
      {}
    );
  }


  public getPdfQuotation(id: number, context?: HttpContext): Observable<Blob> {
    return this.http.get<Blob>(
      environment.BASE_URL_API + 'quotations/' + id + '/pdf',
        {responseType: 'blob' as 'json', context}
    );
  }

  public getPdfInvoice(id: number): Observable<Blob> {
    return this.http.get<Blob>(
      environment.BASE_URL_API + 'quotations/' + id + '/invoice',
        {responseType: 'blob' as 'json'}
    );
  }

  public updateOrderStatus(id: number, status: string): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/order-status',
        {order_status: status}
    );
  }

  public updatePaymentStatus(id: number, status: string): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/payment-status',
        {payment_status: status}
    );
  }

  public exportQuotations(
    filter: IQuotationFilterRequest | undefined,
    search: string | undefined,
    co_pilot: number
    ){
    const params = new HttpParams().set('filters', JSON.stringify(filter)).set('key', search!).set('co_pilot', co_pilot);
    return this.http.get(environment.BASE_URL_API + 'quotations/export', {
      responseType: 'blob', params
    }
    );
  }

  public acceptQuotation(id: number): Observable<APIResponse<IQuotationDetail>> {
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/accept',
      {}
    );
  }

  public getQuotationSourceContent(id: number): Observable<APIResponse<IQuotationSourceContent>> {
    return this.http.get<APIResponse<IQuotationSourceContent>>(
      environment.BASE_URL_API + 'quotations/' + id + '/ai_details'
    );
  }

  /**
   * WB-7337 (front-end companion): fetch just the attachments of a source/parent order, for split
   * children with no documents of their own. This is a silent background best-effort fetch, so it
   * opts out of the global error-toast interceptor and never throws — a failed fetch resolves to
   * `[]` so callers can treat "couldn't borrow attachments" the same as "source order also has none".
   */
  public getFallbackSourceAttachments(sourceId: number): Observable<string[]> {
    return this.http.get<APIResponse<IQuotationSourceContent>>(
      environment.BASE_URL_API + 'quotations/' + sourceId + '/ai_details',
      { context: skipErrorToast() }
    ).pipe(
      map((res) => res.object?.email?.attachments ?? []),
      catchError((err) => {
        console.error(`Failed to fetch fallback source attachments for order ${sourceId}`, err);
        return of([] as string[]);
      }),
    );
  }

  /** WB-6815: per-line document-coordinate index. Optional/non-critical — drives the hover highlights. */
  public getQuotationDocumentCoordinates(id: number): Observable<APIResponse<IDocumentCoordinatesResponse>> {
    return this.http.get<APIResponse<IDocumentCoordinatesResponse>>(
      environment.BASE_URL_API + 'quotations/' + id + '/document_coordinates'
    );
  }

  public updateEmailOrder(id: number, payload: IUpdateEmailOrder): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/edit_ai_details',
      payload
    );
  }

  public updateAiNotes(id: number, payload: IUpdateAiNotes): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/ai_notes',
      payload
    );
  }

  public checkProductAvailability(data: IQuotationAvailabilityReq): Observable<any> {
    const url = environment.BASE_URL_API + `quotations/${data.quotation_id}/check_availability?delivery_date=${data.delivery_date}`;
    return this.http.get<ISentToErpRespone>(url);
  }

  public deleteDuplicateProducts(id: number, payload: IDeleteDuplicateProducts): Observable<APIResponse<IQuotationDetail>> {
    return this.http.patch<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/remove_duplicates',
      payload
    );
  }

  public promptToOrder(id: number, payload: IPromptToOrder): Observable<APIResponse<IQuotationDetail>> {
    // This effect shows its own contextual PROMPT_TO_ORDER_ERROR toast, so opt
    // out of the global interceptor toast to avoid a double toast.
    return this.http.post<APIResponse<IQuotationDetail>>(
      environment.BASE_URL_API + 'quotations/' + id + '/prompt_to_order',
      payload,
      { context: skipErrorToast() }
    );
  }

  public updateQuotationLines(
    id: number,
    payload: IUpdateQuotationLinesPayload,
    linesMetadata?: IPagination | null,
  ): Observable<APIResponse<IUpdateLinesDelta>> {
    const url = environment.BASE_URL_API + 'quotations/' + id + '/lines';
    if (
      linesMetadata != null &&
      linesMetadata.page != null &&
      linesMetadata.limit != null
    ) {
      const params = new HttpParams()
        .set('paginate_lines', 'true')
        .set('page', String(linesMetadata.page))
        .set('limit', String(linesMetadata.limit));
      return this.http.patch<APIResponse<IUpdateLinesDelta>>(url, payload, { params });
    }
    return this.http.patch<APIResponse<IUpdateLinesDelta>>(url, payload);
  }

  public toggleOrderLineAlert(quotationId: number, orderLineId: number, alertId: number): Observable<APIResponse<IOrderLineAlert>> {
    return this.http.patch<APIResponse<IOrderLineAlert>>(
      environment.BASE_URL_API + `quotations/${quotationId}/order-lines/${orderLineId}/alerts/${alertId}/ignore`,
      {}
    );
  }

  public ignoreAllAlerts(quotationId: number, alertIds: number[]): Observable<APIResponse<any>> {
    return this.http.patch<APIResponse<any>>(
      environment.BASE_URL_API + `quotations/${quotationId}/alerts/ignore`,
      { alert_ids: alertIds }
    );
  }

  public getQuotationAlerts(quotationId: number): Observable<APIResponse<IQuotationAlertsResponse>> {
    return this.http.get<APIResponse<IQuotationAlertsResponse>>(
      environment.BASE_URL_API + `quotations/${quotationId}/alerts`
    );
  }

  public getOrderLineAlternatives(quotationId: number, lineId: number): Observable<APIResponse<IParseProduct[]>> {
    return this.http.get<APIResponse<IParseProduct[]>>(
      environment.BASE_URL_API + `quotations/${quotationId}/lines/${lineId}/alternatives`
    );
  }

  /**
   * Fetch alternatives for ALL lines of a quotation in one request.
   * Returns a map keyed by string line ID: `{ "123": [alt, ...], ... }`.
   * The result is cached per quotation so concurrent or sequential callers
   * (one per open dialog) share a single HTTP request.
   */
  public getOrderAlternativesBulk(quotationId: number): Observable<APIResponse<Record<string, IParseProduct[]>>> {
    if (!this._alternativesBulkCache.has(quotationId)) {
      const req$ = this.http.get<APIResponse<Record<string, IParseProduct[]>>>(
        environment.BASE_URL_API + `quotations/${quotationId}/alternatives`
      ).pipe(shareReplay(1));
      this._alternativesBulkCache.set(quotationId, req$);
    }
    return this._alternativesBulkCache.get(quotationId)!;
  }

  /**
   * Fetch alternatives for ALL unmatched lines of a quotation in one request.
   * Returns a map keyed by string unmatched-line ID (the `ai_details.unmatched_order_lines[].id`):
   * `{ "651": [alt, ...], ... }`. Cached per quotation so opening one unmatched line loads the
   * whole map once and subsequent opens reuse it — same behaviour as `getOrderAlternativesBulk`.
   */
  public getUnmatchedAlternativesBulk(quotationId: number): Observable<APIResponse<Record<string, IParseProduct[]>>> {
    if (!this._unmatchedAlternativesBulkCache.has(quotationId)) {
      const req$ = this.http.get<APIResponse<Record<string, IParseProduct[]>>>(
        environment.BASE_URL_API + `quotations/${quotationId}/unmatched-alternatives`
      ).pipe(shareReplay(1));
      this._unmatchedAlternativesBulkCache.set(quotationId, req$);
    }
    return this._unmatchedAlternativesBulkCache.get(quotationId)!;
  }

  /** Evict the cached bulk alternatives (matched + unmatched) for a quotation (call after a line is swapped). */
  public clearAlternativesBulkCache(quotationId: number): void {
    this._alternativesBulkCache.delete(quotationId);
    this._unmatchedAlternativesBulkCache.delete(quotationId);
  }

  public getQuotationHistory(quotationId: number): Observable<APIResponse<IHistory>> {
    return this.http.get<APIResponse<IHistory>>(
      environment.BASE_URL_API + `quotations/${quotationId}/history`
    );
  }

  public getQuotationDeals(quotationId: number): Observable<APIResponse<IQuotationDealsResponse>> {
    return this.http.get<APIResponse<IQuotationDealsResponse>>(
      environment.BASE_URL_API + `quotations/${quotationId}/deals`
    );
  }

  public getOrderLineStock(quotationId: number, lineId: number): Observable<APIResponse<ILineStockResponse>> {
    return this.http.get<APIResponse<ILineStockResponse>>(
      environment.BASE_URL_API + `quotations/${quotationId}/lines/${lineId}/stock`
    );
  }

  public previewSegregationForPartner(orderId: number, partnerId: number, companyId?: number): Observable<any> {
    const body: Record<string, number> = { order_id: orderId, partner_id: partnerId };
    if (companyId) body['company_id'] = companyId;
    return this.http.post<any>(
      environment.BASE_URL_API + 'segregation/preview-for-partner',
      body,
    );
  }

  public segregationSplitInPlace(orderId: number, mechanismId: number): Observable<any> {
    return this.http.post<any>(
      environment.BASE_URL_API + 'segregation/split-in-place',
      { order_id: orderId, mechanism_id: mechanismId },
    );
  }

}
